export type GptLiveVoicePhase = "listening" | "live-response" | "delegating";

const AUDIO_LEVEL_POLL_MS = 100;
const AUDIO_LEVEL_DIAGNOSTIC_INTERVAL_MS = 1_000;
const SPEAKING_AUDIO_LEVEL_THRESHOLD = 0.02;
const SPEAKING_SILENCE_HOLD_MS = 600;
const TRANSCRIPT_TURN_CONTINUITY_TOLERANCE_MS = 400;
const TRANSCRIPT_TURN_MAX_SILENCE_MS = 2_000;

export type GptLiveTranscriptSpeaker = "user" | "assistant";

export interface GptLiveTranscriptTurn {
  batchId: string;
  turnId: string;
  speaker: GptLiveTranscriptSpeaker;
  content: string;
  providerSessionId: string | null;
  providerEventIds: string[];
  startMs: number | null;
  endMs: number | null;
  delegatedAnswerStartedAt?: number | null;
  isFinal: boolean;
  isComplete: boolean;
}

interface TranscriptFragment {
  arrivalSequence: number;
  speaker: GptLiveTranscriptSpeaker;
  delta: string;
  eventId: string | null;
  startMs: number | null;
  endMs: number | null;
  delegatedAnswerStartedAt: number | null;
}

function hasTimelineStartBetween(
  starts: number[],
  lowerExclusive: number,
  upperExclusive: number,
): boolean {
  let low = 0;
  let high = starts.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (starts[middle] <= lowerExclusive) low = middle + 1;
    else high = middle;
  }
  return low < starts.length && starts[low] < upperExclusive;
}

function buildTranscriptTurns(
  fragments: TranscriptFragment[],
  batchId: string,
  providerSessionId: string | null,
  isFinal: boolean,
): GptLiveTranscriptTurn[] {
  let previousArrivalStartMs: number | null = null;
  const ordered = fragments
    .map((fragment) => {
      const effectiveStartMs = fragment.startMs ?? previousArrivalStartMs;
      previousArrivalStartMs = effectiveStartMs;
      return { fragment, effectiveStartMs };
    })
    .sort((left, right) => {
      if (left.effectiveStartMs === null) {
        return right.effectiveStartMs === null
          ? left.fragment.arrivalSequence - right.fragment.arrivalSequence
          : -1;
      }
      if (right.effectiveStartMs === null) return 1;
      return (
        left.effectiveStartMs - right.effectiveStartMs ||
        left.fragment.arrivalSequence - right.fragment.arrivalSequence
      );
    });
  const groupsBySpeaker = new Map<
    GptLiveTranscriptSpeaker,
    Array<{
      fragments: TranscriptFragment[];
      firstStartMs: number | null;
      firstArrivalSequence: number;
      lastEndMs: number | null;
    }>
  >();
  const startsBySpeaker = new Map<GptLiveTranscriptSpeaker, number[]>();
  ordered.forEach(({ fragment, effectiveStartMs }) => {
    const speakerGroups = groupsBySpeaker.get(fragment.speaker) ?? [];
    const currentGroup = speakerGroups.at(-1);
    const effectiveEndMs = fragment.endMs ?? effectiveStartMs;
    const otherSpeaker: GptLiveTranscriptSpeaker =
      fragment.speaker === "user" ? "assistant" : "user";
    const otherSpeakerStarts = startsBySpeaker.get(otherSpeaker) ?? [];
    const otherSpeakerStartedInGap =
      currentGroup !== undefined &&
      effectiveStartMs !== null &&
      currentGroup.lastEndMs !== null &&
      hasTimelineStartBetween(
        otherSpeakerStarts,
        currentGroup.lastEndMs,
        effectiveStartMs,
      );
    const gapMs =
      currentGroup?.lastEndMs !== null &&
      currentGroup?.lastEndMs !== undefined &&
      effectiveStartMs !== null
        ? effectiveStartMs - currentGroup.lastEndMs
        : null;
    const isContinuous =
      currentGroup !== undefined &&
      (effectiveStartMs === null ||
        currentGroup.lastEndMs === null ||
        (gapMs !== null &&
          (gapMs <= TRANSCRIPT_TURN_CONTINUITY_TOLERANCE_MS ||
            (!otherSpeakerStartedInGap &&
              gapMs <= TRANSCRIPT_TURN_MAX_SILENCE_MS))));
    if (isContinuous && currentGroup) {
      currentGroup.fragments.push(fragment);
      if (effectiveEndMs !== null) {
        currentGroup.lastEndMs = Math.max(
          currentGroup.lastEndMs ?? effectiveEndMs,
          effectiveEndMs,
        );
      }
    } else {
      speakerGroups.push({
        fragments: [fragment],
        firstStartMs: effectiveStartMs,
        firstArrivalSequence: fragment.arrivalSequence,
        lastEndMs: effectiveEndMs,
      });
    }
    groupsBySpeaker.set(fragment.speaker, speakerGroups);
    if (effectiveStartMs !== null) {
      const speakerStarts = startsBySpeaker.get(fragment.speaker) ?? [];
      speakerStarts.push(effectiveStartMs);
      startsBySpeaker.set(fragment.speaker, speakerStarts);
    }
  });
  const groups = Array.from(groupsBySpeaker.values())
    .flat()
    .sort((left, right) => {
      if (left.firstStartMs === null) {
        return right.firstStartMs === null
          ? left.firstArrivalSequence - right.firstArrivalSequence
          : -1;
      }
      if (right.firstStartMs === null) return 1;
      return (
        left.firstStartMs - right.firstStartMs ||
        left.firstArrivalSequence - right.firstArrivalSequence
      );
    });
  return groups.map((group) => {
    const first = group.fragments[0];
    const isLastSpeakerGroup =
      groupsBySpeaker.get(first.speaker)?.at(-1) === group;
    const otherSpeaker: GptLiveTranscriptSpeaker =
      first.speaker === "user" ? "assistant" : "user";
    const runEndMs = group.lastEndMs;
    const otherSpeakerStartedAfterRun =
      runEndMs !== null &&
      (startsBySpeaker.get(otherSpeaker) ?? []).some(
        (startMs) => startMs > runEndMs,
      );
    const isComplete =
      isFinal || !isLastSpeakerGroup || otherSpeakerStartedAfterRun;
    const starts = group.fragments
      .map((fragment) => fragment.startMs)
      .filter((value): value is number => value !== null);
    const ends = group.fragments
      .map((fragment) => fragment.endMs)
      .filter((value): value is number => value !== null);
    const hasCompleteStartTimes = group.fragments.every(
      (fragment) => fragment.startMs !== null,
    );
    const hasCompleteEndTimes = group.fragments.every(
      (fragment) => fragment.endMs !== null,
    );
    return {
      batchId,
      turnId: `${batchId}:turn:${first.speaker}:${first.startMs ?? first.arrivalSequence}`,
      speaker: first.speaker,
      content: group.fragments
        .map((fragment) => fragment.delta)
        .join("")
        .replace(/\[[a-z]+(?: [a-z]+){0,2}\](?!\()/g, " ")
        .replace(/[ \t]{2,}/g, " ")
        .trim(),
      providerSessionId,
      providerEventIds: Array.from(
        new Set(
          group.fragments
            .map((fragment) => fragment.eventId)
            .filter((eventId): eventId is string => eventId !== null),
        ),
      ),
      startMs:
        hasCompleteStartTimes && starts.length ? Math.min(...starts) : null,
      endMs: hasCompleteEndTimes && ends.length ? Math.max(...ends) : null,
      delegatedAnswerStartedAt: first.delegatedAnswerStartedAt,
      isComplete,
      // Only the last speaker run can still grow. A later speaker run closes
      // every earlier bubble even before the batch is finalized.
      isFinal: first.speaker === "user" || isComplete,
    };
  });
}
export interface GptLiveVoiceCallbacks {
  onSessionCreated?: (sessionId: string) => void;
  onPhase: (phase: GptLiveVoicePhase) => void;
  onTranscriptTurns?: (
    turns: GptLiveTranscriptTurn[],
    boundary: GptLiveTranscriptBoundary,
  ) => void;
  onToolCallStart?: (callId: string) => void;
  onToolCallComplete?: (callId: string) => void;
  onDelegationStarted?: (callId: string) => void;
  onDelegationAccepted?: (callId: string, submissionId: string) => void;
  onDisconnect?: (
    closeReason: GptLiveCloseReason | null,
    reasonFinal: boolean,
  ) => void;
  onPlaybackError?: () => void;
  onDelegationFailure?: (reasonCode: string) => void;
}

export type GptLiveTranscriptBoundary = "tool-card" | "session-end" | null;

export type GptLiveCloseReason =
  | "close_requested"
  | "expired"
  | "content"
  | "remote_hangup"
  | "connection_lost"
  | "normal_closure"
  | "protocol_error"
  | "provider_closed"
  | "invalid_provider_frame"
  | "event_processing_failed"
  | "lease_lost"
  | "lease_error"
  | "client_ended"
  | "server_shutdown"
  | "max_session_length";

const GPT_LIVE_CLOSE_REASONS = new Set<GptLiveCloseReason>([
  "close_requested",
  "expired",
  "content",
  "remote_hangup",
  "connection_lost",
  "normal_closure",
  "protocol_error",
  "provider_closed",
  "invalid_provider_frame",
  "event_processing_failed",
  "lease_lost",
  "lease_error",
  "client_ended",
  "server_shutdown",
  "max_session_length",
]);

export interface GptLiveVoiceTransport {
  connect(stream: MediaStream, callbacks: GptLiveVoiceCallbacks): Promise<void>;
  completeDelegation(delegationId: string): void;
  end(options?: { notifyServer?: boolean }): Promise<void>;
}

export type GptLiveAuthFetch = (
  endpoint: string,
  tag: string,
  options?: RequestInit,
  timeout?: number,
) => Promise<any>;

export interface GptLiveVoiceConfig {
  conversationId: number;
  workspaceId: string;
  authFetch: GptLiveAuthFetch;
  requestKey: string;
}

type TestTransportFactory = (
  config: GptLiveVoiceConfig,
) => GptLiveVoiceTransport;

declare global {
  interface Window {
    /** Development-only injection point for browser acceptance checks. */
    __IDA_GPT_LIVE_TEST_TRANSPORT_FACTORY__?: TestTransportFactory;
  }
}

function createWebRtcTransport(
  config: GptLiveVoiceConfig,
): GptLiveVoiceTransport {
  let peer: RTCPeerConnection | null = null;
  let dataChannel: RTCDataChannel | null = null;
  let sessionId: string | null = null;
  let activeCallbacks: GptLiveVoiceCallbacks | null = null;
  let completeActiveDelegation: ((delegationId: string) => void) | null = null;
  let transcriptBatchId: string | null = null;
  let transcriptBatchSequence = 0;
  let transcriptArrivalSequence = 0;
  let transcriptFragments: TranscriptFragment[] = [];
  let delegationStartedAt: number | null = null;
  let delegatedAnswerStartedAt: number | null = null;
  const seenTranscriptEventIds = new Set<string>();
  let disconnectReconciliationStarted = false;
  let outputAudio: HTMLAudioElement | null = null;
  let audioLevelPollTimer: ReturnType<typeof setTimeout> | null = null;
  let lastAudioDiagnosticAt = 0;
  let currentPhase: GptLiveVoicePhase | null = null;
  let lastAudibleAt: number | null = null;
  const pendingDelegations = new Map<string, { submissionId: string | null }>();
  const activeCompositionCallIds = new Set<string>();
  let remoteAudioSpeaking = false;
  let audioLevelMonitoring = false;
  const inboundAudioEnergyById = new Map<
    string,
    { totalAudioEnergy: number; totalSamplesDuration: number }
  >();
  const transport: GptLiveVoiceTransport = {
    async connect(stream, callbacks) {
      activeCallbacks = callbacks;
      transcriptBatchId = null;
      transcriptBatchSequence = 0;
      transcriptArrivalSequence = 0;
      transcriptFragments = [];
      delegationStartedAt = null;
      delegatedAnswerStartedAt = null;
      pendingDelegations.clear();
      seenTranscriptEventIds.clear();
      currentPhase = null;
      const setPhase = (phase: GptLiveVoicePhase) => {
        // A connection-ready or provider-completion event can race with an
        // active delegated chat request. Keep the UI in "working" until every
        // delegation has reached its correlated terminal event.
        if (phase === "listening" && pendingDelegations.size > 0) return;
        if (currentPhase === phase) return;
        currentPhase = phase;
        callbacks.onPhase(phase);
      };
      const publishTranscriptBatch = (
        boundary: "tool-card" | "session-end" | null = null,
      ) => {
        if (!transcriptFragments.length || !transcriptBatchId) return;
        callbacks.onTranscriptTurns?.(
          buildTranscriptTurns(
            transcriptFragments,
            transcriptBatchId,
            sessionId,
            boundary !== null,
          ),
          boundary,
        );
        if (boundary !== null) {
          transcriptFragments = [];
          transcriptBatchId = null;
        }
      };
      completeActiveDelegation = (delegationId) => {
        const callId = pendingDelegations.has(delegationId)
          ? delegationId
          : [...pendingDelegations].find(
              ([, delegation]) => delegation.submissionId === delegationId,
            )?.[0];
        if (!callId) return;
        pendingDelegations.delete(callId);
        publishTranscriptBatch("tool-card");
        if (pendingDelegations.size === 0) {
          delegatedAnswerStartedAt = delegationStartedAt ?? Date.now();
          delegationStartedAt = null;
        }
        if (activeCompositionCallIds.delete(callId)) {
          callbacks.onToolCallComplete?.(callId);
        }
        if (pendingDelegations.size === 0) setPhase("listening");
      };
      const addTranscriptFragment = (
        speaker: GptLiveTranscriptSpeaker,
        eventPayload: Record<string, unknown>,
      ) => {
        const delta = eventPayload.delta;
        if (typeof delta !== "string" || delta.length === 0) return;
        const eventId =
          typeof eventPayload.event_id === "string"
            ? eventPayload.event_id
            : null;
        if (eventId && seenTranscriptEventIds.has(eventId)) return;
        if (eventId) seenTranscriptEventIds.add(eventId);
        transcriptArrivalSequence += 1;
        const startMs =
          typeof eventPayload.start_ms === "number" &&
          Number.isFinite(eventPayload.start_ms)
            ? eventPayload.start_ms
            : null;
        const endMs =
          typeof eventPayload.end_ms === "number" &&
          Number.isFinite(eventPayload.end_ms)
            ? eventPayload.end_ms
            : null;
        if (process.env.NODE_ENV !== "production") {
          console.debug("[IDA GPT-Live] transcript delta", {
            event_type:
              speaker === "user"
                ? "session.input_transcript.delta"
                : "session.output_transcript.delta",
            start_ms: startMs,
            end_ms: endMs,
            arrival_sequence: transcriptArrivalSequence,
            fragment_length: delta.length,
          });
        }
        transcriptFragments.push({
          arrivalSequence: transcriptArrivalSequence,
          speaker,
          delta,
          eventId,
          startMs,
          endMs,
          delegatedAnswerStartedAt:
            speaker === "assistant" ? delegatedAnswerStartedAt : null,
        });
        if (!transcriptBatchId) {
          transcriptBatchSequence += 1;
          transcriptBatchId = `${sessionId ?? "session"}:batch:${transcriptBatchSequence}`;
        }
        publishTranscriptBatch();
      };
      if (!config.workspaceId || !Number.isSafeInteger(config.conversationId)) {
        throw new Error("Select an IDA conversation before starting voice.");
      }
      peer = new RTCPeerConnection();
      stream.getAudioTracks().forEach((track) => peer?.addTrack(track, stream));
      dataChannel = peer.createDataChannel("oai-events");
      outputAudio = new Audio();
      outputAudio.autoplay = true;
      const startOutputPlayback = () => {
        if (!outputAudio) return;
        void outputAudio.play().catch(() => callbacks.onPlaybackError?.());
      };
      const pollRemoteAudioLevel = async () => {
        const activePeer = peer;
        if (!activePeer || !outputAudio?.srcObject) return;
        try {
          const stats = await activePeer.getStats();
          let maxAudioLevel: number | null = null;
          let maxAudioLevelSource: "audioLevel" | "energy" | null = null;
          stats.forEach((report) => {
            if (
              report.type !== "inbound-rtp" ||
              (report.kind !== "audio" && report.mediaType !== "audio")
            ) {
              return;
            }

            let audioLevel =
              typeof report.audioLevel === "number" ? report.audioLevel : null;
            let audioLevelSource: "audioLevel" | "energy" | null =
              audioLevel === null ? null : "audioLevel";
            const totalAudioEnergy = report.totalAudioEnergy;
            const totalSamplesDuration = report.totalSamplesDuration;
            if (
              audioLevel === null &&
              typeof totalAudioEnergy === "number" &&
              typeof totalSamplesDuration === "number"
            ) {
              const statsId = report.id ?? "inbound-audio";
              const previous = inboundAudioEnergyById.get(statsId);
              if (
                previous &&
                totalAudioEnergy >= previous.totalAudioEnergy &&
                totalSamplesDuration > previous.totalSamplesDuration
              ) {
                const energyDelta =
                  totalAudioEnergy - previous.totalAudioEnergy;
                const durationDelta =
                  totalSamplesDuration - previous.totalSamplesDuration;
                audioLevel = Math.sqrt(energyDelta / durationDelta);
                audioLevelSource = "energy";
              }
              inboundAudioEnergyById.set(statsId, {
                totalAudioEnergy,
                totalSamplesDuration,
              });
            }
            if (audioLevel !== null && Number.isFinite(audioLevel)) {
              if (maxAudioLevel === null || audioLevel > maxAudioLevel) {
                maxAudioLevel = audioLevel;
                maxAudioLevelSource = audioLevelSource;
              }
            }
          });
          if (peer !== activePeer) return;
          const now = Date.now();
          if (
            maxAudioLevel !== null &&
            maxAudioLevel >= SPEAKING_AUDIO_LEVEL_THRESHOLD
          ) {
            lastAudibleAt = now;
            remoteAudioSpeaking = true;
            // Phase can be reset by another transport event while this same
            // audio remains audible (for example, connection-ready racing the
            // first poll). Reassert it from the current stats sample.
            if (pendingDelegations.size === 0) setPhase("live-response");
          } else if (
            pendingDelegations.size === 0 &&
            remoteAudioSpeaking &&
            lastAudibleAt !== null &&
            now - lastAudibleAt >= SPEAKING_SILENCE_HOLD_MS
          ) {
            remoteAudioSpeaking = false;
            lastAudibleAt = null;
            setPhase("listening");
          }
          if (
            process.env.NODE_ENV === "development" &&
            now - lastAudioDiagnosticAt >= AUDIO_LEVEL_DIAGNOSTIC_INTERVAL_MS
          ) {
            lastAudioDiagnosticAt = now;
            console.debug("[IDA GPT-Live] remote audio level", {
              level: maxAudioLevel,
              source: maxAudioLevelSource,
              phase: currentPhase,
            });
          }
        } catch {
          // Audio level stats are optional; session phase handling continues.
        }
        if (audioLevelMonitoring && peer === activePeer) {
          audioLevelPollTimer = setTimeout(
            () => void pollRemoteAudioLevel(),
            AUDIO_LEVEL_POLL_MS,
          );
        }
      };
      peer.addEventListener("track", (event) => {
        outputAudio!.srcObject =
          event.streams[0] ?? new MediaStream([event.track]);
        startOutputPlayback();
        if (audioLevelPollTimer === null && !audioLevelMonitoring) {
          audioLevelMonitoring = true;
          void pollRemoteAudioLevel();
        }
      });
      peer.addEventListener("connectionstatechange", () => {
        if (
          peer?.connectionState === "failed" ||
          peer?.connectionState === "disconnected"
        ) {
          if (disconnectReconciliationStarted) return;
          disconnectReconciliationStarted = true;
          const disconnectedSessionId = sessionId;
          callbacks.onDisconnect?.(null, false);
          if (!disconnectedSessionId) {
            callbacks.onDisconnect?.(null, true);
            return;
          }
          void (async () => {
            let closeReason: GptLiveCloseReason | null = null;
            try {
              const response = (await config.authFetch(
                `/api/workspace/ida/gpt-live/conversations/${config.conversationId}/sessions/${encodeURIComponent(disconnectedSessionId)}/reconcile`,
                "reconcile GPT-Live voice session",
                { headers: { "X-Workspace-Id": config.workspaceId } },
                10_000,
              )) as { close_reason?: unknown };
              closeReason =
                typeof response?.close_reason === "string" &&
                GPT_LIVE_CLOSE_REASONS.has(
                  response.close_reason as GptLiveCloseReason,
                )
                  ? (response.close_reason as GptLiveCloseReason)
                  : null;
            } catch {
              // End the local session even when reconciliation is unavailable.
            }
            try {
              await transport.end();
            } catch {
              // Reconciliation already captured the provider's close reason.
            }
            callbacks.onDisconnect?.(closeReason, true);
          })();
        }
      });
      dataChannel.onmessage = (event) => {
        const failActiveDelegation = (reasonCode: string) => {
          if (pendingDelegations.size === 0) return;
          pendingDelegations.clear();
          activeCompositionCallIds.clear();
          setPhase("listening");
          callbacks.onDelegationFailure?.(reasonCode);
        };
        try {
          const payload = JSON.parse(String(event.data)) as {
            type?: unknown;
            event?: {
              type?: unknown;
              response_id?: unknown;
              call_id?: unknown;
              item?: { type?: unknown; call_id?: unknown };
              response?: { id?: unknown };
            };
            response_id?: unknown;
            call_id?: unknown;
            item?: { type?: unknown; call_id?: unknown };
            response?: { id?: unknown };
          };
          if (typeof payload.type !== "string") {
            const error = new Error(
              "GPT-Live data channel event has no event type",
            );
            console.error(
              error,
              "Failed to process GPT-Live data channel event",
            );
            failActiveDelegation("invalid_provider_event");
            return;
          }
          const responseEvent =
            payload.type === "response.event" ? payload.event : payload;
          const responseType =
            payload.type === "response.event"
              ? payload.event?.type
              : payload.type;
          if (responseType === "error") {
            const errorPayload = (
              responseEvent as { error?: { code?: unknown } } | undefined
            )?.error;
            const reasonCode =
              typeof errorPayload?.code === "string" &&
              /^[A-Za-z0-9_.-]{1,80}$/.test(errorPayload.code)
                ? errorPayload.code
                : "provider_error";
            const error = new Error("GPT-Live data channel returned an error");
            console.error(
              error,
              `Failed to process GPT-Live data channel event (${reasonCode})`,
            );
            failActiveDelegation(reasonCode);
            return;
          }
          if (responseType === "response.completed") {
            remoteAudioSpeaking = false;
            lastAudibleAt = null;
            if (
              currentPhase === "live-response" &&
              pendingDelegations.size === 0
            ) {
              // GPT-Live does not send a separate audio-ended event.
              setPhase("listening");
            }
          }
          if (
            responseType === "session.input_transcript.delta" &&
            pendingDelegations.size === 0
          ) {
            remoteAudioSpeaking = false;
            setPhase("listening");
          }
          const responseItem = (
            responseEvent as
              | {
                  item?: {
                    type?: unknown;
                    name?: unknown;
                    call_id?: unknown;
                    output?: unknown;
                  };
                }
              | undefined
          )?.item;
          if (
            responseItem?.type === "function_call" &&
            typeof responseItem.call_id === "string"
          ) {
            publishTranscriptBatch("tool-card");
            activeCompositionCallIds.add(responseItem.call_id);
            callbacks.onToolCallStart?.(responseItem.call_id);
          }
          if (responseType === "session.input_transcript.delta") {
            addTranscriptFragment(
              "user",
              responseEvent as Record<string, unknown>,
            );
            delegationStartedAt = null;
            delegatedAnswerStartedAt = null;
          } else if (responseType === "session.output_transcript.delta") {
            addTranscriptFragment(
              "assistant",
              responseEvent as Record<string, unknown>,
            );
          }
          if (
            responseItem?.type === "function_call" &&
            responseItem.name === "submit_chat_request"
          ) {
            delegationStartedAt = Date.now();
            delegatedAnswerStartedAt = null;
            remoteAudioSpeaking = false;
            const callId =
              typeof responseItem.call_id === "string"
                ? responseItem.call_id
                : null;
            if (callId) {
              pendingDelegations.set(callId, { submissionId: null });
              callbacks.onDelegationStarted?.(callId);
            }
            setPhase("delegating");
          }
          if (
            responseItem?.type === "function_call_output" &&
            typeof responseItem.call_id === "string" &&
            activeCompositionCallIds.has(responseItem.call_id) &&
            !pendingDelegations.has(responseItem.call_id)
          ) {
            callbacks.onToolCallComplete?.(responseItem.call_id);
            activeCompositionCallIds.delete(responseItem.call_id);
          }
          if (
            responseItem?.type === "function_call_output" &&
            typeof responseItem.call_id === "string" &&
            pendingDelegations.has(responseItem.call_id)
          ) {
            if (typeof responseItem.output === "string") {
              try {
                const acceptedOutput = JSON.parse(responseItem.output) as {
                  submission_id?: unknown;
                };
                if (
                  typeof acceptedOutput.submission_id === "string" &&
                  acceptedOutput.submission_id.length > 0
                ) {
                  const delegation = pendingDelegations.get(
                    responseItem.call_id,
                  );
                  if (!delegation) return;
                  delegation.submissionId = acceptedOutput.submission_id;
                  callbacks.onDelegationAccepted?.(
                    responseItem.call_id,
                    acceptedOutput.submission_id,
                  );
                }
              } catch {
                // Keep delegating if the accepted result has no usable identity.
              }
            }
          }
        } catch {
          const error = new Error(
            "Could not parse a GPT-Live data channel event",
          );
          console.error(error, "Failed to process GPT-Live data channel event");
          failActiveDelegation("invalid_provider_event");
        }
      };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      if (!peer.localDescription?.sdp)
        throw new Error("The browser could not create an audio offer.");
      const endpoint = `/api/workspace/ida/gpt-live/conversations/${config.conversationId}/sessions`;
      const response = (await config.authFetch(
        endpoint,
        "start GPT-Live voice",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/sdp",
            "Idempotency-Key": config.requestKey,
            "X-Workspace-Id": config.workspaceId,
          },
          body: peer.localDescription.sdp,
        },
        45_000,
      )) as { session_id?: unknown; sdp?: unknown };
      if (
        typeof response?.session_id !== "string" ||
        typeof response.sdp !== "string"
      ) {
        throw new Error(
          "The voice service returned an invalid session response.",
        );
      }
      sessionId = response.session_id;
      callbacks.onSessionCreated?.(sessionId);
      await peer.setRemoteDescription({ type: "answer", sdp: response.sdp });

      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const finish = (error?: Error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (error) reject(error);
          else resolve();
        };
        const timer = setTimeout(
          () => finish(new Error("Voice connection timed out.")),
          20_000,
        );
        const checkConnected = () => {
          if (
            peer?.connectionState === "failed" ||
            peer?.connectionState === "closed"
          ) {
            finish(new Error("The voice connection was interrupted."));
          } else if (
            peer?.connectionState === "connected" &&
            dataChannel?.readyState === "open"
          ) {
            setPhase("listening");
            finish();
          }
        };
        peer?.addEventListener("connectionstatechange", checkConnected);
        dataChannel?.addEventListener("open", checkConnected);
        checkConnected();
      });
    },
    completeDelegation(delegationId) {
      completeActiveDelegation?.(delegationId);
    },
    async end(options = {}) {
      if (transcriptFragments.length && transcriptBatchId) {
        activeCallbacks?.onTranscriptTurns?.(
          buildTranscriptTurns(
            transcriptFragments,
            transcriptBatchId,
            sessionId,
            true,
          ),
          "session-end",
        );
        transcriptFragments = [];
        transcriptBatchId = null;
      }
      if (audioLevelPollTimer !== null) {
        clearTimeout(audioLevelPollTimer);
        audioLevelPollTimer = null;
      }
      audioLevelMonitoring = false;
      lastAudibleAt = null;
      pendingDelegations.clear();
      activeCompositionCallIds.clear();
      remoteAudioSpeaking = false;
      const activeSessionId = sessionId;
      sessionId = null;
      dataChannel?.close();
      dataChannel = null;
      outputAudio?.pause();
      if (outputAudio) outputAudio.srcObject = null;
      outputAudio = null;
      activeCallbacks = null;
      completeActiveDelegation = null;
      peer?.close();
      peer = null;
      if (activeSessionId && options.notifyServer !== false) {
        await config.authFetch(
          `/api/workspace/ida/gpt-live/conversations/${config.conversationId}/sessions/${encodeURIComponent(activeSessionId)}/end`,
          "end GPT-Live voice",
          {
            method: "POST",
            headers: { "X-Workspace-Id": config.workspaceId },
          },
        );
      }
    },
  };
  return transport;
}

/** Create the production WebRTC transport, with a dev-only harness injection point. */
export function createGptLiveVoiceTransport(
  config: GptLiveVoiceConfig,
): GptLiveVoiceTransport {
  if (
    process.env.NODE_ENV !== "production" &&
    typeof window !== "undefined" &&
    window.__IDA_GPT_LIVE_TEST_TRANSPORT_FACTORY__
  ) {
    return window.__IDA_GPT_LIVE_TEST_TRANSPORT_FACTORY__(config);
  }
  return createWebRtcTransport(config);
}
